import React, { useState, useCallback, useMemo } from 'react';
import { COLORS, SPACING, FONT_SIZES, BORDER_RADIUS } from '../data/constants';
import VideoPlayer from '../components/VideoPlayer';
import UserMoments from '../components/UserMoments';
import PriceChart from '../components/PriceChart';
import { generateCSV, downloadCSV } from '../utils/csvExport';

function formatVideoTime(seconds) {
  if (seconds == null || seconds < 0) return '0:00';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function findClosestDataPoint(data, targetTimestamp) {
  if (!data || data.length === 0) return null;
  let best = data[0];
  let bestDiff = Math.abs(data[0].timestamp - targetTimestamp);
  for (let i = 1; i < data.length; i++) {
    const diff = Math.abs(data[i].timestamp - targetTimestamp);
    if (diff < bestDiff) {
      best = data[i];
      bestDiff = diff;
    } else {
      break;
    }
  }
  return best;
}

// Convert a unix timestamp to a datetime-local string (for input value)
function unixToDatetimeLocal(unix) {
  const d = new Date(unix * 1000);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Convert a datetime-local string back to unix timestamp
function datetimeLocalToUnix(str) {
  return Math.floor(new Date(str).getTime() / 1000);
}

const UsageView = ({ data }) => {
  const { marketInfo, videoInfo, analytics } = data;
  const [seekToTimestamp, setSeekToTimestamp] = useState(null);
  const [currentVideoTimestamp, setCurrentVideoTimestamp] = useState(0);
  const [userMoments, setUserMoments] = useState([]);

  const fullData = useMemo(
    () => analytics?.analytics?.minutelyProbabilities || [],
    [analytics]
  );
  const startTimestamp = analytics?.analytics?.summary?.timeRange?.start || null;

  // View range state — default to full data bounds
  const [viewStart, setViewStart] = useState('');
  const [viewEnd, setViewEnd] = useState('');

  // Compute the actual bounds from data
  const dataBoundsStart = fullData.length > 0 ? fullData[0].timestamp : null;
  const dataBoundsEnd = fullData.length > 0 ? fullData[fullData.length - 1].timestamp : null;

  // Filter data by user-chosen view range
  const filteredData = useMemo(() => {
    if (fullData.length === 0) return fullData;
    const startCutoff = viewStart ? datetimeLocalToUnix(viewStart) : -Infinity;
    const endCutoff = viewEnd ? datetimeLocalToUnix(viewEnd) : Infinity;
    const filtered = fullData.filter(
      (p) => p.timestamp >= startCutoff && p.timestamp <= endCutoff
    );
    return filtered.length > 0 ? filtered : fullData;
  }, [fullData, viewStart, viewEnd]);

  // Moment handlers
  const handleAddMoment = useCallback(() => {
    if (currentVideoTimestamp == null || !startTimestamp) return;
    const unixTime = startTimestamp + currentVideoTimestamp;
    const closest = findClosestDataPoint(fullData, unixTime);
    const newMoment = {
      id: Date.now(),
      videoTimestamp: currentVideoTimestamp,
      videoTimestampFormatted: formatVideoTime(currentVideoTimestamp),
      note: '',
      timestamp: unixTime,
      probability: closest?.probability || 0,
    };
    setUserMoments((prev) =>
      [...prev, newMoment].sort((a, b) => a.videoTimestamp - b.videoTimestamp)
    );
  }, [currentVideoTimestamp, startTimestamp, fullData]);

  const handleDeleteMoment = useCallback((id) => {
    setUserMoments((prev) => prev.filter((m) => m.id !== id));
  }, []);

  const handleEditMoment = useCallback((id, newNote) => {
    setUserMoments((prev) =>
      prev.map((m) => (m.id === id ? { ...m, note: newNote } : m))
    );
  }, []);

  const handleMomentClick = useCallback((moment) => {
    setSeekToTimestamp(moment.videoTimestamp);
  }, []);

  const handleTimeUpdate = useCallback((currentTime) => {
    setCurrentVideoTimestamp(currentTime);
  }, []);

  const handleSeekFromChart = useCallback((videoTime) => {
    setSeekToTimestamp(videoTime);
  }, []);

  const handleDownloadCSV = useCallback(() => {
    const csv = generateCSV(fullData, userMoments);
    const safeName = (marketInfo?.question || 'polyvideo')
      .slice(0, 40)
      .replace(/[^a-zA-Z0-9]/g, '_');
    downloadCSV(csv, `polyvideo-${safeName}.csv`);
  }, [fullData, userMoments, marketInfo]);

  const handleResetView = useCallback(() => {
    setViewStart('');
    setViewEnd('');
  }, []);

  // No data state
  if (!analytics || !analytics.analytics) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <div>
            <h1 style={styles.title}>{marketInfo.question}</h1>
            <p style={styles.subtitle}>{videoInfo.title}</p>
          </div>
          <button style={styles.headerBtn} onClick={() => window.location.reload()}>
            New Analysis
          </button>
        </div>
        <div style={styles.noDataContainer}>
          <div style={styles.noDataCard}>
            <h2 style={styles.noDataTitle}>No Data Available</h2>
            <p style={styles.noDataMessage}>
              {analytics?.message ||
                'No data found in the specified time range. Try a different time range.'}
            </p>
            <button style={styles.headerBtn} onClick={() => window.location.reload()}>
              Try Different Time Range
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="usage-container" style={styles.container}>
      {/* Header */}
      <div className="usage-header" style={styles.header}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1 className="usage-title" style={styles.title}>{marketInfo.question}</h1>
          <p style={styles.subtitle}>{videoInfo.title}</p>
        </div>
        <div className="usage-header-actions" style={styles.headerActions}>
          <button style={styles.headerBtn} onClick={handleDownloadCSV}>
            Download CSV
          </button>
          <button style={styles.headerBtn} onClick={() => window.location.reload()}>
            New Analysis
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="usage-content" style={styles.content}>
        {/* Video Panel */}
        <div className="usage-video" style={styles.leftPanel}>
          <VideoPlayer
            videoInfo={videoInfo}
            seekToTimestamp={seekToTimestamp}
            onTimeUpdate={handleTimeUpdate}
          />
        </div>

        {/* Chart + View Range Panel */}
        <div className="usage-chart" style={styles.rightPanel}>
          <PriceChart
            data={filteredData}
            marketQuestion={marketInfo.question}
            userMoments={userMoments}
            currentVideoTimestamp={currentVideoTimestamp}
            startTimestamp={startTimestamp}
            onSeekToTime={handleSeekFromChart}
          />

          {/* View Range Controls */}
          <div style={styles.rangeContainer}>
            <div style={styles.rangeHeader}>
              <span style={styles.rangeTitle}>View Range</span>
              {(viewStart || viewEnd) && (
                <button style={styles.resetBtn} onClick={handleResetView}>
                  Reset
                </button>
              )}
            </div>
            <div className="range-row" style={styles.rangeRow}>
              <div style={styles.rangeField}>
                <label style={styles.rangeLabel}>From</label>
                <input
                  type="datetime-local"
                  value={viewStart || (dataBoundsStart ? unixToDatetimeLocal(dataBoundsStart) : '')}
                  min={dataBoundsStart ? unixToDatetimeLocal(dataBoundsStart) : undefined}
                  max={dataBoundsEnd ? unixToDatetimeLocal(dataBoundsEnd) : undefined}
                  onChange={(e) => setViewStart(e.target.value)}
                  style={styles.rangeInput}
                />
              </div>
              <div className="range-dash" style={styles.rangeDash}>&ndash;</div>
              <div style={styles.rangeField}>
                <label style={styles.rangeLabel}>To</label>
                <input
                  type="datetime-local"
                  value={viewEnd || (dataBoundsEnd ? unixToDatetimeLocal(dataBoundsEnd) : '')}
                  min={dataBoundsStart ? unixToDatetimeLocal(dataBoundsStart) : undefined}
                  max={dataBoundsEnd ? unixToDatetimeLocal(dataBoundsEnd) : undefined}
                  onChange={(e) => setViewEnd(e.target.value)}
                  style={styles.rangeInput}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Notes Panel - comes last on mobile (after chart) */}
        <div className="usage-notes" style={styles.notesPanel}>
          <UserMoments
            moments={userMoments}
            onAddMoment={handleAddMoment}
            onDeleteMoment={handleDeleteMoment}
            onEditMoment={handleEditMoment}
            onMomentClick={handleMomentClick}
            currentVideoTimestamp={currentVideoTimestamp}
          />
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#0f1319',
    color: COLORS.WHITE,
    padding: SPACING.LG,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '16px',
    marginBottom: SPACING.XL,
    paddingBottom: SPACING.LG,
    borderBottom: '1px solid rgba(255,255,255,0.08)',
  },
  title: {
    fontSize: '22px',
    fontWeight: '700',
    color: '#fff',
    margin: '0 0 4px 0',
    lineHeight: 1.3,
  },
  subtitle: {
    fontSize: FONT_SIZES.SM,
    color: '#6b7280',
    margin: 0,
  },
  headerActions: {
    display: 'flex',
    gap: '8px',
    flexShrink: 0,
  },
  headerBtn: {
    padding: '8px 16px',
    fontSize: '13px',
    fontWeight: '600',
    color: '#d1d5db',
    backgroundColor: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    fontFamily: 'inherit',
    whiteSpace: 'nowrap',
  },
  content: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: SPACING.XL,
  },
  leftPanel: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  rightPanel: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  // View range controls
  rangeContainer: {
    backgroundColor: '#141722',
    borderRadius: '12px',
    padding: '16px 20px',
    border: '1px solid rgba(255,255,255,0.06)',
  },
  rangeHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '10px',
  },
  rangeTitle: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#d1d5db',
  },
  resetBtn: {
    padding: '3px 10px',
    fontSize: '11px',
    fontWeight: '600',
    color: '#6b7280',
    backgroundColor: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '4px',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  rangeRow: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: '8px',
  },
  rangeField: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    flex: 1,
  },
  rangeLabel: {
    fontSize: '11px',
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
    fontWeight: '500',
  },
  rangeInput: {
    padding: '7px 8px',
    fontSize: '12px',
    color: '#e5e7eb',
    backgroundColor: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '6px',
    outline: 'none',
    fontFamily: 'inherit',
    colorScheme: 'dark',
    minWidth: 0,
    width: '100%',
  },
  rangeDash: {
    color: '#4b5563',
    fontSize: '16px',
    paddingBottom: '8px',
    userSelect: 'none',
  },
  notesPanel: {
    minWidth: 0,
  },
  // No data
  noDataContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '60vh',
    padding: SPACING.XL,
  },
  noDataCard: {
    backgroundColor: 'rgba(255,255,255,0.04)',
    borderRadius: BORDER_RADIUS.LARGE,
    padding: SPACING.XXL,
    textAlign: 'center',
    maxWidth: '600px',
    border: '1px solid rgba(255,255,255,0.06)',
  },
  noDataTitle: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#fff',
    marginBottom: SPACING.LG,
  },
  noDataMessage: {
    fontSize: FONT_SIZES.MD,
    color: '#6b7280',
    lineHeight: '1.6',
    marginBottom: SPACING.XL,
  },
};

export default UsageView;
